import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpClientModule } from '@angular/common/http';
import * as i2 from '@ngx-translate/core';
import { TranslateService, TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { Observable } from 'rxjs';

class InternationalizationService {
    constructor(http, translate, internationalizationAPIUrl, defaultLang) {
        this.http = http;
        this.translate = translate;
        this.internationalizationAPIUrl = internationalizationAPIUrl;
        this.defaultLang = defaultLang;
        if (localStorage.getItem('userlang') != null) {
            // set as default language the user language the language on the localStorage
            this.translate.setDefaultLang(localStorage.getItem('userlang'));
            this.translate.use(localStorage.getItem('userlang'));
        }
        else {
            // set as default language a specific language
            this.translate.setDefaultLang(this.defaultLang);
            this.translate.use(this.defaultLang);
            localStorage.setItem('userlang', this.defaultLang);
        }
        // set the current available translation
        this.getAvailableLanguages().subscribe(langs => {
            this.translate.addLangs(langs);
        });
    }
    // get available languages
    getAvailableLanguages() {
        return this.http.get(this.internationalizationAPIUrl + 'availableLanguages');
    }
    // change language
    changeLanguage(locale) {
        this.translate.use(locale);
        localStorage.setItem('userlang', locale);
    }
    // get current language in use
    getCurrentLanguage() {
        return this.translate.currentLang;
    }
}
InternationalizationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: InternationalizationService, deps: [{ token: i1.HttpClient }, { token: i2.TranslateService }, { token: 'internationalizationAPIUrl' }, { token: 'defaultLang' }], target: i0.ɵɵFactoryTarget.Injectable });
InternationalizationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: InternationalizationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: InternationalizationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: i2.TranslateService }, { type: undefined, decorators: [{
                        type: Inject,
                        args: ['internationalizationAPIUrl']
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: ['defaultLang']
                    }] }];
    } });

class LocalStorageTranslateLoader {
    constructor(http, internationalizationAPIUrl) {
        this.http = http;
        this.internationalizationAPIUrl = internationalizationAPIUrl;
    }
    // this method is used by TranslateLoader to load the translation JSON
    getTranslation(locale) {
        return new Observable(obs => {
            // get the version of the translations on DB for the selected locale
            this.getCurrentVersion(locale).subscribe(currentVersion => {
                // check if the version of the translations on the local storage
                if (currentVersion.toString() === this.getSavedVersion(locale) && localStorage.getItem(locale) != null) {
                    // load translation from the local storage
                    obs.next(JSON.parse(localStorage.getItem(locale)));
                    obs.complete();
                }
                else {
                    // load translation from DB.
                    this.getCurrentTranslations(locale).subscribe(translations => {
                        // setting the translation  on local storage.
                        this.setLocalStorage(translations, locale);
                        obs.next(translations.messages);
                        obs.complete();
                    });
                }
            });
        });
    }
    // set version and translation in localStorage
    setLocalStorage(translation, locale) {
        localStorage.setItem(locale + '_version', translation.version);
        localStorage.setItem(locale, JSON.stringify(translation.messages));
    }
    // get the current version of translation of locale from the local storage
    getSavedVersion(locale) {
        return localStorage.getItem(locale + '_version');
    }
    // create headers
    createHeaders() {
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json');
        return headers;
    }
    // get translation of locale from DB
    getCurrentTranslations(locale) {
        return this.http
            .get(this.internationalizationAPIUrl + 'currentTranslations', {
            headers: this.createHeaders(),
            params: new HttpParams().append('locale', locale)
        });
    }
    // get version of locale from DB
    getCurrentVersion(locale) {
        return this.http
            .get(this.internationalizationAPIUrl + 'currentVersion', {
            headers: this.createHeaders(),
            params: new HttpParams().append('locale', locale)
        });
    }
}
LocalStorageTranslateLoader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: LocalStorageTranslateLoader, deps: [{ token: i1.HttpClient }, { token: 'internationalizationAPIUrl' }], target: i0.ɵɵFactoryTarget.Injectable });
LocalStorageTranslateLoader.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: LocalStorageTranslateLoader });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: LocalStorageTranslateLoader, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Inject,
                        args: ['internationalizationAPIUrl']
                    }] }];
    } });

class InternationalizationModule {
    static forRoot(internationalizationAPIUrl, defaultLang) {
        return {
            ngModule: InternationalizationModule,
            providers: [
                TranslateService,
                {
                    provide: 'internationalizationAPIUrl',
                    useValue: internationalizationAPIUrl + 'api/internationalization/'
                },
                {
                    provide: 'defaultLang',
                    useValue: defaultLang
                }
            ]
        };
    }
}
InternationalizationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: InternationalizationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
InternationalizationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: InternationalizationModule, imports: [HttpClientModule, i2.TranslateModule], exports: [TranslateModule] });
InternationalizationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: InternationalizationModule, providers: [InternationalizationService], imports: [[
            HttpClientModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useClass: LocalStorageTranslateLoader
                },
                isolate: true
            }),
        ], TranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.0", ngImport: i0, type: InternationalizationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule,
                        TranslateModule.forRoot({
                            loader: {
                                provide: TranslateLoader,
                                useClass: LocalStorageTranslateLoader
                            },
                            isolate: true
                        }),
                    ],
                    providers: [InternationalizationService],
                    exports: [TranslateModule]
                }]
        }] });

/*
 * Public API Surface of internationalization
 */

/**
 * Generated bundle index. Do not edit.
 */

export { InternationalizationModule, InternationalizationService };
